import { HttpAgent } from '@ag-ui/client'
import { Amplify } from 'aws-amplify'
import { confirmSignIn, fetchAuthSession, signIn, signOut } from 'aws-amplify/auth'
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { invocationsUrl, loadUiConfig, type UiConfig } from './config.js'

type ChatMessage = {
  id: string
  role: 'user' | 'assistant'
  text: string
}

/** One screen: sign in, then talk. Everything the agent says arrives as AG-UI events. */
export function App() {
  const [config, setConfig] = useState<UiConfig | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    loadUiConfig()
      .then((loaded) => {
        Amplify.configure({
          Auth: {
            Cognito: { userPoolId: loaded.userPoolId, userPoolClientId: loaded.userPoolClientId },
          },
        })
        setConfig(loaded)
        return fetchAuthSession()
      })
      .then((session) => setToken(session?.tokens?.accessToken?.toString() ?? null))
      .catch((e: unknown) => setError(String(e)))
  }, [])

  if (error) return <p className="error">{error}</p>
  if (!config) return <p>Lade Konfiguration …</p>
  if (!token) return <SignIn onSignedIn={setToken} />
  return (
    <Chat config={config} token={token} onSignOut={() => signOut().then(() => setToken(null))} />
  )
}

function SignIn({ onSignedIn }: { onSignedIn: (token: string) => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [needsNewPassword, setNeedsNewPassword] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  async function finish() {
    const session = await fetchAuthSession()
    const accessToken = session.tokens?.accessToken?.toString()
    if (!accessToken) throw new Error('Kein Access Token in der Session')
    onSignedIn(accessToken)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setMessage(null)
    try {
      // Cognito mails a temporary password; the first sign-in has to replace it.
      const result = needsNewPassword
        ? await confirmSignIn({ challengeResponse: newPassword })
        : await signIn({ username: email, password })
      if (result.isSignedIn) return await finish()
      if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true)
        setMessage('Erstes Anmelden: bitte ein neues Passwort setzen.')
        return
      }
      setMessage(`Weiterer Schritt nötig: ${result.nextStep.signInStep}`)
    } catch (e: unknown) {
      setMessage(String(e))
    }
  }

  return (
    <form className="signin" onSubmit={submit}>
      <h1>harness</h1>
      <input
        type="email"
        placeholder="E-Mail"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="username"
        disabled={needsNewPassword}
        required
      />
      <input
        type="password"
        placeholder={needsNewPassword ? 'Temporäres Passwort' : 'Passwort'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        disabled={needsNewPassword}
        required
      />
      {needsNewPassword && (
        <input
          type="password"
          placeholder="Neues Passwort"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
      )}
      <button type="submit">{needsNewPassword ? 'Passwort setzen' : 'Anmelden'}</button>
      {message && <p className="error">{message}</p>}
    </form>
  )
}

function Chat({
  config,
  token,
  onSignOut,
}: {
  config: UiConfig
  token: string
  onSignOut: () => void
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const endRef = useRef<HTMLLIElement>(null)

  // One AG-UI agent per conversation: it keeps the thread, the session id keeps the microVM.
  const agent = useMemo(
    () =>
      new HttpAgent({
        url: invocationsUrl(config),
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id': crypto.randomUUID(),
        },
        threadId: crypto.randomUUID(),
      }),
    [config, token],
  )

  // The newest bubble keeps itself in view: scrolled on every growth, typing included
  // (origin's ChatThread). Without the deps the thread stops following the answer.
  // biome-ignore lint/correctness/useExhaustiveDependencies: growth is the signal.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages, busy])

  async function send(text: string) {
    const trimmed = text.trim()
    if (!trimmed || busy) return
    setBusy(true)
    const userId = crypto.randomUUID()
    const assistantId = crypto.randomUUID()
    setMessages((m) => [
      ...m,
      { id: userId, role: 'user', text: trimmed },
      { id: assistantId, role: 'assistant', text: '' },
    ])
    agent.messages = [...agent.messages, { id: userId, role: 'user', content: trimmed }]
    const patch = (fn: (msg: ChatMessage) => ChatMessage) =>
      setMessages((m) => m.map((msg) => (msg.id === assistantId ? fn(msg) : msg)))
    try {
      await agent.runAgent(
        {},
        {
          onTextMessageContentEvent: ({ event }) => {
            patch((msg) => ({ ...msg, text: msg.text + event.delta }))
          },
          onRunErrorEvent: ({ event }) => {
            patch((msg) => ({ ...msg, text: `Fehler: ${event.message}` }))
          },
        },
      )
    } catch (e: unknown) {
      patch((msg) => ({ ...msg, text: `Fehler: ${String(e)}` }))
    } finally {
      setBusy(false)
    }
  }

  function onFormSubmit(event: FormEvent) {
    event.preventDefault()
    const text = input
    setInput('')
    void send(text)
  }

  return (
    <main className="mx-auto flex h-full max-w-3xl flex-col">
      <header className="flex items-baseline justify-between border-[var(--line)] border-b px-4 py-3">
        <span className="font-medium">harness</span>
        <button type="button" className="text-[var(--muted)] text-sm" onClick={onSignOut}>
          Abmelden
        </button>
      </header>

      <ul
        role="log"
        aria-label="Gespräch"
        className="flex flex-1 flex-col gap-2 overflow-y-auto px-4 py-3"
      >
        {messages.map((m) => (
          <li
            key={m.id}
            className={
              m.role === 'user'
                ? 'flex flex-col items-end gap-2'
                : 'flex flex-col items-start gap-2'
            }
          >
            {m.text && (
              <div
                className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-[15px] leading-6 ${
                  m.role === 'user'
                    ? 'bg-[var(--bubble-mine)] text-[var(--fg)]'
                    : 'border border-[var(--line)] bg-[var(--bubble-theirs)] text-[var(--fg)]'
                }`}
              >
                {m.text}
              </div>
            )}
          </li>
        ))}
        <li ref={endRef} aria-hidden="true" />
      </ul>

      <form
        onSubmit={onFormSubmit}
        className="flex flex-col gap-2 border-[var(--line)] border-t p-4"
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Was beschäftigt dich?"
          rows={3}
          className="resize-none rounded-lg border border-[var(--line)] bg-[var(--bubble-theirs)] p-3 text-[15px] text-[var(--fg)]"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              e.currentTarget.form?.requestSubmit()
            }
          }}
        />
        <button
          type="submit"
          disabled={busy}
          className="self-end rounded-lg bg-[var(--qidt-btn-bg)] px-4 py-2 text-[var(--qidt-btn-fg)] disabled:opacity-50"
        >
          Senden
        </button>
      </form>
    </main>
  )
}
